import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { Pane, toaster, ListIcon, Text, Spinner } from 'evergreen-ui';
import { useMutation, useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { getSettings } from '../../queries/settings';
import { DayFilterCard, Spacer, Header, Switch, Support } from '../../components';
import { updateSettings } from '../../queries/settings';
import { SettingsIcon } from '../../components/icons';
import { setSettings } from '../../store/settingsSlice';
import Subscription from './Subscription';
import { useNavigate } from 'react-router-dom';
import { StyledLink } from './styles';

const days = ['s', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const removeSeconds = time => {
  return time.split(':')[0] + ':' + time.split(':')[1];
};

const checkEndTime = end => {
  const splitted = end.split(':');
  if (splitted[0] === '00' && splitted[1] === '00') return '24:00';
  return end;
};

const revertToZeroIfDayEnd = end => {
  if (end === '24:00') return '00:00';
  return end;
};

const stateInitialValue = values => {
  return days.reduce((acc, day) => {
    if (values) {
      const dayFilter = values.user_day_filters.find(item => item.day === day);
      if (dayFilter) {
        acc[day] = {
          filter: [removeSeconds(dayFilter.start), checkEndTime(removeSeconds(dayFilter.end))],
          active: !dayFilter.rejectAll,
        };
      }
    }
    if (!acc[day]) {
      acc[day] = { filter: ['00:00', '24:00'], active: false };
    }
    return acc;
  }, {});
};

const alignedTiming = (minutesString, timeString) => {
  let minutes = Number.parseInt(minutesString);
  let tail = minutes < 15 || minutes > 45 ? '00' : '30';
  let hours = Number.parseInt(timeString.slice(0, -3));
  if (minutes > 45) {
    hours++;
  }
  return `${hours.toString().padStart(2, '0')}:${tail}`;
};

const Filters = ({ user, settings, updateSettingsQuery, token }) => {
  const [dayFilters, setDayFilters] = useState(() => stateInitialValue(settings));
  const [enabled, setEnabled] = useState(settings.enabled);
  const [savingStatus, setSavingStatus] = useState('');
  const savingFuncs = useRef();
  savingFuncs.current = { setSavingStatus };
  const callUpdateSettings = useCallback(
    (dayFilters, enabled) => {
      const dayFiltersToSend = Object.keys(dayFilters).map(day => {
        return {
          day,
          start: `${dayFilters[day].filter[0]}:00.000`,
          end: `${revertToZeroIfDayEnd(dayFilters[day].filter[1])}:00.000`,
          rejectAll: !dayFilters[day].active,
        };
      });
      const call = async () => {
        if (savingFuncs.current.setSavingStatus) {
          savingFuncs.current.setSavingStatus('Saving...');
        }
        await updateSettingsQuery.mutateAsync({
          enabled,
          dayFilters: dayFiltersToSend,
          token,
        });
        if (savingFuncs.current.setSavingStatus) {
          savingFuncs.current.setSavingStatus('Changes Saved');
        }
      };
      call();
    },
    [token, updateSettingsQuery],
  );

  useEffect(() => {
    let df = { ...dayFilters };
    let misalignedTimings = false;
    Object.keys(dayFilters).forEach(day => {
      let [start, end] = dayFilters[day].filter;
      let s = start.slice(-2);
      if (s !== '00' && s !== '30') {
        misalignedTimings = true;
        df[day].filter[0] = alignedTiming(s, start);
      }
      let e = end.slice(-2);
      if (e !== '00' && e !== '30') {
        misalignedTimings = true;
        df[day].filter[1] = alignedTiming(e, end);
      }
    });

    if (misalignedTimings) {
      callUpdateSettings(df, enabled);
    }
  }, [dayFilters, callUpdateSettings, enabled]);

  useEffect(() => {
    let timeout;
    if (savingStatus === 'Saving Changes in 3')
      timeout = setTimeout(() => setSavingStatus('Saving Changes in 2'), 1000);
    if (savingStatus === 'Saving Changes in 2')
      timeout = setTimeout(() => setSavingStatus('Saving Changes in 1'), 1000);
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [savingStatus]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCallUpdateSettings = useCallback(debounce(callUpdateSettings, 3000), [callUpdateSettings]);
  // call update on unmount
  useEffect(() => {
    return () => {
      debouncedCallUpdateSettings.flush();
    };
  }, [debouncedCallUpdateSettings]);
  return (
    <>
      <Pane>
        <Text padding={24} paddingTop={0} color="#D7DCE9">
          Download our Android app{' '}
          <StyledLink
            target="_blank"
            href="https://play.google.com/store/apps/details?id=com.grabkings.app"
            rel="noreferrer"
          >
            HERE
          </StyledLink>
        </Text>
      </Pane>
      <Subscription settings={settings} user={user} />
      <Pane display="flex" justifyContent="flex-end">
        <Text padding={!savingStatus ? 24 : 16} paddingTop={0} color="#D7DCE9">
          {savingStatus}
        </Text>
      </Pane>
      <Pane display="flex" justifyContent="space-between" alignItems="center" background="#262E34" padding={24}>
        <Text fontWeight={700} color="#D7DCE9">{`System ${enabled ? 'Running' : 'OFF'}`}</Text>
        <Switch
          checked={enabled}
          disabled={!settings.hasAPaidSubscription}
          onChange={checked => {
            setEnabled(checked);
            debouncedCallUpdateSettings.cancel();
            debouncedCallUpdateSettings(dayFilters, checked);
            setSavingStatus('Saving Changes in 3');
            // callUpdateSettings(dayFilters, checked);
          }}
        />
      </Pane>
      {days.map((day, index) => (
        <>
          <Spacer height={6} />
          <DayFilterCard
            disabled={!settings.hasAPaidSubscription}
            dayOfTheWeek={day.substr(0, 3)}
            value={dayFilters[day].filter}
            setValue={value => {
              dayFilters[day].filter = value;
              setDayFilters({ ...dayFilters });
              debouncedCallUpdateSettings.cancel();
              debouncedCallUpdateSettings(dayFilters, enabled);
              setSavingStatus('Saving Changes in 3');
              // callUpdateSettings(dayFilters, enabled);
            }}
            active={dayFilters[day].active}
            setActive={checked => {
              dayFilters[day].active = checked;
              setDayFilters({ ...dayFilters });
              // callUpdateSettings(dayFilters, enabled);
              debouncedCallUpdateSettings.cancel();
              debouncedCallUpdateSettings(dayFilters, enabled);
              setSavingStatus('Saving Changes in 3');
            }}
          />
        </>
      ))}
    </>
  );
};

const FiltersPage = () => {
  const user = useSelector(state => state.authentication.user);
  const token = useSelector(state => state.authentication.token);
  const settings = useSelector(state => state.settings.settings);
  const navigate = useNavigate();
  const result = useQuery('settings', () => getSettings(token, user), { cacheTime: 0 });
  const dispatch = useDispatch();
  // useEffect(() => {
  //   if (result.data) {
  //     dispatch(setSettings(result.data));
  //   }
  // }, [dispatch, result.data]);
  // useEffect(() => {
  //   return () => dispatch(setSettings(null));
  // }, [dispatch]);

  const updateSettingsQuery = useMutation(updateSettings, {
    useErrorBoundary: true,
    onSuccess: data => {
      if (!data || !data.updated) toaster.danger('Something went wrong', { duration: 5 });
    },
  });

  return (
    <Pane display="flex" flexDirection="column" minHeight="100%">
      <Header
        subTitle={user.email}
        RightIcon={<ListIcon color="#F0F0F2" size={24} />}
        onRightIconClick={() => navigate('/app/logs')}
        LeftIcon={<SettingsIcon color="rgba(255,255,255,0.5)" opacity={0.5} size={20} />}
        padding={16}
        extendedLogo
      />
      {settings ? (
        <>
          <Filters user={user} settings={settings} updateSettingsQuery={updateSettingsQuery} token={token} />
        </>
      ) : (
        <Pane flex="1" display="flex" justifyContent="center" alignItems="center">
          <Spinner size={100} />
        </Pane>
      )}
      <Support />
    </Pane>
  );
};

export default FiltersPage;
